import { action, computed, observable } from 'mobx';
import ApiService from '@app/Services/ApiService';

import { ApiUrls, SearchFilters } from '@app/AppConstants';
import { PersonModel} from '@app/Models';

import { CompletionType, PromiseCompletion } from '@app/Classes/PromiseCompletion';
import { MatchComparer, stringSearchService } from '@app/Services/StringSearchService';
import { navigationStore } from '@app/Stores/NavigationStore';

import { AzureAuth } from '@app/Components/AzureAuth';
import { NotificationHandler } from '@app/Components/ToastNotification';

export type PersonModelWithSearchTerms = PersonModel & {
	terms?: string[];
	hasExactCodeMatch?: boolean;
};

export default class PersonStore {
	@observable personList: PersonModel[] = [];
	@observable selectedPersonId: string | null;
	@observable dataLoading: PromiseCompletion = new PromiseCompletion(CompletionType.Completed);
	@observable selectedPerson: PersonModel | null;
	@observable searchPersonString: string = '';
	
	@observable isSearchedByCode: boolean = true;
	@observable isSearchedById: boolean = true;
	@observable isSearchedByFirstName: boolean = true;
	@observable isSearchedByLastName: boolean = true;
	@observable isSearchedWithExactMatch: boolean = false;
    @observable isSearchedWithInactive: boolean = false;
    @observable isSearchedWithDeleted: boolean = false;

	@action
	public async loadPersons() {
		const { data: persons } = await ApiService.getTypedData<PersonModel[]>(ApiUrls.PersonUrl, null, { completion: this.dataLoading });
		this.personList = persons;
		if (AzureAuth.isUserOnlyInCrewGroup && this.personList.length === 1) {
			navigationStore.redirect('/person/' + this.personList[0].code);
		}
	}
	
	@action
    selectPersonByCode(code: string) {
		this.selectedPerson = this.personList.find((person: PersonModel) => person.code.toUpperCase() === code.toUpperCase()) || null;
	}

	@action.bound
	onSearchTermChanged(_?: React.ChangeEvent<HTMLInputElement>, newValue?: string)  {
        this.searchPersonString = newValue || '';
    }

	@action
	switchCheckbox = (filter: string) => {
		switch (filter) {
			case SearchFilters.code:
				this.isSearchedByCode = !this.isSearchedByCode;
				break;
			case SearchFilters.id:
				this.isSearchedById = !this.isSearchedById;
				break;
			case SearchFilters.firstName:
				this.isSearchedByFirstName = !this.isSearchedByFirstName;
				break;
			case SearchFilters.lastName:
				this.isSearchedByLastName = !this.isSearchedByLastName;
				break;
			case SearchFilters.exactMatch:
				this.isSearchedWithExactMatch = !this.isSearchedWithExactMatch;
				break;
			case SearchFilters.inactiveInclude:
				this.isSearchedWithInactive = !this.isSearchedWithInactive;
                break;
            case SearchFilters.deletedInclude:
                this.isSearchedWithDeleted = !this.isSearchedWithDeleted;
                break;
			default:
				break;
		}	
    };

    @action.bound
    async sendPersonToMb(personId: string) {
        await ApiService.postTypedData(ApiUrls.PersonUrl + '/' + personId + '/sendtomb');
        NotificationHandler.showSuccess('Person has been sent', 'Success message');
    }
    
    @action.bound
	async onDeletePersonCallback(){
		this.selectedPerson = null;
		await this.loadPersons();
	}

	private _getFilteredPersonList(personList: PersonModelWithSearchTerms[]) {
		let filteredList: PersonModelWithSearchTerms[] = personList;

		const searchTerms = this.searchTerms;
		const comparator = this.textComparer;

		if (!this.isSearchedWithInactive) {
			filteredList = filteredList.filter((person: PersonModel) => person.hasActualContract);
		}

		if (!this.isSearchedWithDeleted) {
			filteredList = filteredList.filter((person: PersonModel) => !person.isDeleted);
		}

		if (searchTerms.length) {
			let filteredBySearchTermsList: PersonModelWithSearchTerms[] = [];

			filteredList.forEach((person: PersonModelWithSearchTerms) => {
				let personWithSearchTerms = {...person};

				for (const term of searchTerms) {
					let everySubTermMatch = true;
					
					const subTerms = term.split(/[ ]+/);
					for (const subTerm of subTerms) {
						let anyMatch = false;

						if (this.isSearchedByFirstName && comparator(person.firstName, subTerm, 0) !== -1) {
							anyMatch = true;
						}
						if (this.isSearchedByLastName && comparator(person.lastName, subTerm, 0) !== -1) {
							anyMatch = true;
						}
						if (this.isSearchedByCode && comparator(person.code, subTerm, 0) !== -1) {
							const isExactMatch = subTerm.toUpperCase() === person.code.toUpperCase();
							if (isExactMatch) personWithSearchTerms.hasExactCodeMatch = true;

							anyMatch = true;
						}
						if (this.isSearchedById && comparator(person.id, subTerm, 0) !== -1) {
							anyMatch = true;
						}

						if (!anyMatch) {
							everySubTermMatch = false;
						}
					}

					if (everySubTermMatch) {
						const isPushed = !!filteredBySearchTermsList.find(p => p.id === person.id);

						if (isPushed) {
							filteredBySearchTermsList = filteredBySearchTermsList.map(p => {
								if (p.id === person.id) {
									const terms = p.terms || [];
									p.terms = [...terms, ...subTerms];
								}

								return p;
							});
						} else {
							personWithSearchTerms.terms = subTerms;
							filteredBySearchTermsList.push(personWithSearchTerms);
						}
					}
				}

				filteredList = filteredBySearchTermsList;
			});
		}

		return filteredList;
	}

	private _getSortedPersonList(personList: PersonModelWithSearchTerms[]) {
		const byCode = (p1: PersonModelWithSearchTerms, p2: PersonModelWithSearchTerms) => {
			const code1 = p1.code.toUpperCase(); // ignore upper and lowercase
			const code2 = p2.code.toUpperCase(); // ignore upper and lowercase

			if (code1 < code2) return -1;
			if (code1 > code2) return 1;
			return 0; // names must be equal
		};
		
		if (this.isSearchedByCode) {
			const exactCodeList: PersonModelWithSearchTerms[] = [];
			const notExactCodeList: PersonModelWithSearchTerms[] = [];

			personList.forEach(person => {
				person.hasExactCodeMatch ? exactCodeList.push(person) : notExactCodeList.push(person);
			});

			return [...exactCodeList.sort(byCode), ...notExactCodeList.sort(byCode)];
		}

		return personList.sort(byCode);
	}

	@computed 
	get textComparer(){
		const comparator: MatchComparer = (v, t, s) => {
			if (this.isSearchedWithExactMatch){
				return s === 0 && stringSearchService.prepareString(v) === stringSearchService.prepareString(t) ? 0 : -1;
			}

			return stringSearchService.prepareString(v)?.indexOf(stringSearchService.prepareString(t) ?? '', s) ?? -1;
		};

		return comparator;
	}

	@computed
	get searchTerms() {
		return this.searchPersonString.split(/[,]+/).map(x => x.trim()).filter(x => !!x);
	}

	@computed
	get filteredList() {
		let filteredList: PersonModelWithSearchTerms[] = this.personList;

		filteredList = this._getFilteredPersonList(filteredList);

		const pathname = navigationStore.currentLocation && navigationStore.currentLocation.pathname || '';
		if (pathname) {
			filteredList.forEach((person: PersonModel) => {
				const toUrl = '/person/' + person.code;
				// if (pathname === toUrl) this.selectedPersonId = person.id;
				if (pathname.toLowerCase() === toUrl.toLowerCase()) this.selectedPersonId = person.id;
			});
		}

		return this._getSortedPersonList(filteredList);
	}
}